/**
 * @voltxampere/math — Complex matrix + vector primitives (Sprint 20)
 *
 * AC analysis (Sprint 20+) needs to factor the complex admittance
 * matrix Y(jω) = G + jωC at every frequency point. Sprint 4's
 * `Complex {re, im}` object is great for arithmetic but allocating
 * one per matrix entry is too costly for sweeps with hundreds of
 * frequencies × dozens of nodes.
 *
 * This module ships the Float64Array-backed counterpart that the
 * complex LU and AC stamps will lean on. Two public types:
 *
 *  - {@link ComplexMatrix}: 2D, row-major, interleaved storage.
 *    Cell (i,j) lives at `data[2 * (i * cols + j)]` (real part)
 *    and `data[2 * (i * cols + j) + 1]` (imaginary part).
 *
 *  - {@link ComplexVector}: 1D, interleaved. Entry `i` lives at
 *    `data[2*i]` and `data[2*i + 1]`.
 *
 * Layout choice (interleaved vs split re/im arrays) is documented
 * in ADR-0038. Interleaved wins because every read is one
 * contiguous 16-byte fetch — cache lines stay packed, the stamp
 * inner loop touches a single typed array, and the LU back-solve
 * never has to dereference a second buffer.
 *
 * Conventions mirror `dense/matrix.ts`:
 *  - Float64Array everywhere — `number[]` is forbidden (CLAUDE.md).
 *  - `?? 0` fallbacks satisfy `noUncheckedIndexedAccess` without the
 *    banned `!` non-null assertion.
 *  - All public functions validate inputs and either return a fresh
 *    allocation or mutate explicitly through the named setters.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
function assertDimsValid(rows, cols, op) {
    if (!Number.isInteger(rows) || !Number.isInteger(cols) || rows < 0 || cols < 0) {
        throw new ContractViolationError(`${op}: rows and cols must be non-negative integers (got ${String(rows)}x${String(cols)})`);
    }
}
function assertVectorLengthValid(length, op) {
    if (!Number.isInteger(length) || length < 0) {
        throw new ContractViolationError(`${op}: length must be a non-negative integer (got ${String(length)})`);
    }
}
function assertMatrixIndex(m, i, j, op) {
    if (!Number.isInteger(i) ||
        !Number.isInteger(j) ||
        i < 0 ||
        j < 0 ||
        i >= m.rows ||
        j >= m.cols) {
        throw new ContractViolationError(`${op}: index (${String(i)}, ${String(j)}) out of bounds for ${String(m.rows)}x${String(m.cols)}`);
    }
}
function assertVectorIndex(v, i, op) {
    if (!Number.isInteger(i) || i < 0 || i >= v.length) {
        throw new ContractViolationError(`${op}: index ${String(i)} out of bounds for length ${String(v.length)}`);
    }
}
function assertFinitePair(re, im, op) {
    if (!Number.isFinite(re) || !Number.isFinite(im)) {
        throw new ContractViolationError(`${op}: non-finite complex (re=${String(re)}, im=${String(im)})`);
    }
}
/** Allocate a zero-initialised complex matrix. */
export function createComplexMatrix(rows, cols) {
    assertDimsValid(rows, cols, "createComplexMatrix");
    return { rows, cols, data: new Float64Array(2 * rows * cols) };
}
/** Allocate a zero-initialised complex vector. */
export function createComplexVector(length) {
    assertVectorLengthValid(length, "createComplexVector");
    return { length, data: new Float64Array(2 * length) };
}
/** Read cell (i,j) as a fresh `Complex`. */
export function complexMatrixGet(m, i, j) {
    assertMatrixIndex(m, i, j, "complexMatrixGet");
    const base = 2 * (i * m.cols + j);
    return { re: m.data[base] ?? 0, im: m.data[base + 1] ?? 0 };
}
/** Overwrite cell (i,j). */
export function complexMatrixSet(m, i, j, c) {
    assertMatrixIndex(m, i, j, "complexMatrixSet");
    assertFinitePair(c.re, c.im, "complexMatrixSet");
    const base = 2 * (i * m.cols + j);
    m.data[base] = c.re;
    m.data[base + 1] = c.im;
}
/** Accumulate `c` into cell (i,j) (additive — for stamp-style assembly). */
export function complexMatrixAdd(m, i, j, c) {
    assertMatrixIndex(m, i, j, "complexMatrixAdd");
    assertFinitePair(c.re, c.im, "complexMatrixAdd");
    const base = 2 * (i * m.cols + j);
    m.data[base] = (m.data[base] ?? 0) + c.re;
    m.data[base + 1] = (m.data[base + 1] ?? 0) + c.im;
}
/**
 * Accumulate raw re/im scalars into cell (i,j). Avoids the per-call
 * `Complex` object allocation when the caller already has the parts
 * as numbers. Hot path during MNA stamp build — the inner loop of
 * `buildACMatrix` goes through this rather than `complexMatrixAdd`.
 */
export function complexMatrixAddRaw(m, i, j, re, im) {
    assertMatrixIndex(m, i, j, "complexMatrixAddRaw");
    assertFinitePair(re, im, "complexMatrixAddRaw");
    const base = 2 * (i * m.cols + j);
    m.data[base] = (m.data[base] ?? 0) + re;
    m.data[base + 1] = (m.data[base + 1] ?? 0) + im;
}
/** Read entry `i` as a fresh `Complex`. */
export function complexVectorGet(v, i) {
    assertVectorIndex(v, i, "complexVectorGet");
    const base = 2 * i;
    return { re: v.data[base] ?? 0, im: v.data[base + 1] ?? 0 };
}
/** Overwrite entry `i`. */
export function complexVectorSet(v, i, c) {
    assertVectorIndex(v, i, "complexVectorSet");
    assertFinitePair(c.re, c.im, "complexVectorSet");
    const base = 2 * i;
    v.data[base] = c.re;
    v.data[base + 1] = c.im;
}
/** Accumulate `c` into entry `i` (additive). */
export function complexVectorAdd(v, i, c) {
    assertVectorIndex(v, i, "complexVectorAdd");
    assertFinitePair(c.re, c.im, "complexVectorAdd");
    const base = 2 * i;
    v.data[base] = (v.data[base] ?? 0) + c.re;
    v.data[base + 1] = (v.data[base + 1] ?? 0) + c.im;
}
/** Accumulate raw re/im scalars (avoids `Complex` allocation on hot paths). */
export function complexVectorAddRaw(v, i, re, im) {
    assertVectorIndex(v, i, "complexVectorAddRaw");
    assertFinitePair(re, im, "complexVectorAddRaw");
    const base = 2 * i;
    v.data[base] = (v.data[base] ?? 0) + re;
    v.data[base + 1] = (v.data[base + 1] ?? 0) + im;
}
/** Deep copy a complex matrix (fresh data buffer). */
export function complexMatrixClone(m) {
    const copy = createComplexMatrix(m.rows, m.cols);
    copy.data.set(m.data);
    return copy;
}
/** Deep copy a complex vector. */
export function complexVectorClone(v) {
    const copy = createComplexVector(v.length);
    copy.data.set(v.data);
    return copy;
}
//# sourceMappingURL=array.js.map